import { useState } from 'react'
import { AlertCircle, FileText, MapPin } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { useActiveContract } from '@/features/auth/hooks/useActiveContract'
import { useBollette } from '@/lib/graphql/hooks'
import { BillCard } from './components/BillCard'
import { BillTrendChart } from './components/BillTrendChart'
import { BillTrendChartSkeleton } from './components/BillTrendChart.skeleton'
import { Pagination } from './components/Pagination'
import { StateLegend } from './components/StateLegend'

const ITEMS_PER_PAGE = 5

export function InvoicesPage() {
  const { cliente, activeContract, isLoading: contractLoading, error: contractError } =
    useActiveContract()

  const codiceCliente = cliente?.codice_cliente
  const codiceContratto = activeContract?.codice_contratto

  const {
    data: bollette,
    isLoading: bolletteLoading,
    error: bolletteError,
  } = useBollette(codiceCliente, codiceContratto)

  const [page, setPage] = useState(0)

  const showSkeleton = contractLoading || bolletteLoading
  const error = contractError ?? bolletteError

  const addressLabel =
    activeContract?.pod.etichetta ?? activeContract?.pod.indirizzo ?? null
  const podCode = activeContract?.pod.pod ?? null

  const totalPages = bollette ? Math.ceil(bollette.length / ITEMS_PER_PAGE) : 0
  const pagedInvoices = bollette
    ? bollette.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE)
    : []

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <header>
        <h1 className="text-2xl font-bold text-foreground md:text-3xl">Bollette</h1>
        {showSkeleton ? (
          <Skeleton className="mt-2 h-4 w-72" />
        ) : addressLabel ? (
          <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            <span className="truncate">
              {addressLabel}
              {podCode ? ` · ${podCode}` : ''}
            </span>
          </p>
        ) : null}
      </header>

      {error ? (
        <EmptyState
          icon={<AlertCircle className="h-5 w-5 text-destructive" />}
          title="Non siamo riusciti a caricare le bollette"
          description={error.message}
        />
      ) : (
        <>
          {/* Trend chart */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Andamento ultime bollette</CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              {showSkeleton ? (
                <BillTrendChartSkeleton />
              ) : (
                <BillTrendChart invoices={bollette} />
              )}
            </CardContent>
          </Card>

          {/* Legenda stati: nascosta quando non ci sono bollette */}
          {!showSkeleton && bollette && bollette.length > 0 && <StateLegend />}

          {/* Storico */}
          <section className="flex flex-col gap-3">
            <h2 className="text-base font-semibold text-foreground">Storico bollette</h2>

            {showSkeleton ? (
              <BillListSkeleton />
            ) : !bollette || bollette.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-5 w-5" />}
                title="Non hai ancora ricevuto una bolletta"
                description="Le bollette appariranno qui non appena saranno emesse."
              />
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  {pagedInvoices.map((inv) => (
                    <BillCard key={inv.codice} invoice={inv} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                )}
              </>
            )}
          </section>
        </>
      )}
    </div>
  )
}

function BillListSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <Card key={i} className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-24" />
            </div>
            <div className="flex flex-col items-end gap-2">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-5 w-24 rounded-full" />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-3 w-16" />
          </div>
        </Card>
      ))}
    </div>
  )
}
